import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';

import { Icon, Typography } from '@repo/ui';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useHasOverlayAnswers from 'services/account/useHasOverlayAnswers';
import { getHasModalOpened, setModalOpened } from 'store/modal';

import { questionRegistry } from './questions';
import type { QuestionKey } from './questions';

import styles from './AudienceFeedbackOverlay.module.css';

type AudienceFeedbackOverlayProps = {
  kitId: string;
  feedbackType: string;
  questions?: QuestionKey[];
  isOpen?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  ignoreNpsAnswers?: boolean;
  extraInfo?: Record<string, unknown>;
};

type Step = 'question' | 'thanks';

export const AudienceFeedbackOverlay = ({
  kitId,
  feedbackType,
  questions,
  isOpen,
  defaultOpen,
  onOpenChange,
  ignoreNpsAnswers = false,
  extraInfo,
}: AudienceFeedbackOverlayProps) => {
  const dispatch = useDispatch();
  const overlayAnswers = useHasOverlayAnswers(feedbackType, {
    includeNps: !ignoreNpsAnswers,
  });
  const { trackEvent } = useAnalytics();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const outerRef = useRef<HTMLDivElement | null>(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [internalOpen, setInternalOpen] = useState(!!defaultOpen);
  const [keyboardOffsetPx, setKeyboardOffsetPx] = useState(0);

  const isControlled = isOpen !== undefined;

  const questionKeys = useMemo<QuestionKey[]>(
    () => questions ?? ['ActionPlanQuestion', 'NpsQuestion', 'NpsTextQuestion'],
    [questions],
  );
  const hasQuestions = questionKeys.length > 0;
  const initialStep: Step = hasQuestions ? 'question' : 'thanks';

  const open = isControlled ? !!isOpen : internalOpen;

  const [step, setStep] = useState<Step>(initialStep);

  const modalIdentifier = useMemo(
    () => ({ name: 'audienceFeedbackOverlay', props: { feedbackType } }),
    [feedbackType],
  );

  const isDismissed = useSelector(getHasModalOpened(modalIdentifier));
  const shouldHide =
    isDismissed || (!overlayAnswers.isLoading && overlayAnswers.hasAnswers);

  const close = () => {
    dispatch(setModalOpened(modalIdentifier));
    if (!isControlled) setInternalOpen(false);
    onOpenChange?.(false);

    setStep(initialStep);
    setQuestionIndex(0);
  };

  useEffect(() => {
    if (shouldHide && open) {
      if (!isControlled) setInternalOpen(false);
      onOpenChange?.(false);
    }

    if (!open) return;

    const t = window.setTimeout(() => {
      containerRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(t);
    };
  }, [isControlled, onOpenChange, open, shouldHide]);

  useEffect(() => {
    if (!open) return;

    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      // On mobile browsers (notably Android Chrome), the on-screen keyboard reduces
      // the visual viewport height but fixed-position elements can remain anchored
      // to the layout viewport. We compute the "covered" bottom area and lift the
      // overlay above it.
      const coveredBottom = Math.max(
        0,
        window.innerHeight - (vv.height + vv.offsetTop),
      );

      // Avoid micro-jitter from fractional px changes.
      setKeyboardOffsetPx(Math.round(coveredBottom));
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const root = outerRef.current;
    if (!root) return;

    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      if (
        target.tagName === 'TEXTAREA' ||
        (target.tagName === 'INPUT' &&
          (target as HTMLInputElement).type !== 'checkbox')
      ) {
        // Let the keyboard open first, then ensure the focused control is visible.
        window.setTimeout(() => {
          target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }, 50);
      }
    };

    root.addEventListener('focusin', onFocusIn);
    return () => {
      root.removeEventListener('focusin', onFocusIn);
    };
  }, [open]);

  const onQuestionDone = () => {
    const currentKey = questionKeys[questionIndex];
    trackEvent('Feedback Toaster Answer', {
      question: currentKey,
      context: feedbackType,
    });
    const isLast = questionIndex >= questionKeys.length - 1;
    if (isLast) {
      setStep('thanks');
      return;
    }
    setQuestionIndex(i => i + 1);
  };

  const questionMeta = useMemo(() => {
    if (step !== 'question') return null;
    if (questionKeys.length === 0) return null;

    return { current: questionIndex + 1, total: questionKeys.length };
  }, [questionIndex, questionKeys.length, step]);

  const CurrentQuestion = useMemo(() => {
    const key = questionKeys[questionIndex];
    if (!key) return null;
    return questionRegistry[key] ?? null;
  }, [questionIndex, questionKeys]);

  if (overlayAnswers.isLoading) return null;
  if (shouldHide) return null;

  // Base offset is defined by CSS (.outer bottom). We add keyboard offset on top.
  const baseBottomPx = window.matchMedia('(min-width: 992px)').matches
    ? 24
    : 10;
  const outerStyle = {
    bottom: `calc(${baseBottomPx}px + ${keyboardOffsetPx}px)`,
  } as const;

  // Keep the container within the visible viewport when keyboard is open.
  // 16px gives a little breathing room so it doesn't touch the top.
  const maxContainerHeight = `calc(100dvh - ${baseBottomPx}px - ${keyboardOffsetPx}px - 16px)`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={styles.outer}
          style={outerStyle}
          ref={outerRef}
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 420, damping: 36 }}
        >
          <div
            className={styles.container}
            style={{ maxHeight: maxContainerHeight }}
            role='dialog'
            aria-modal='false'
            tabIndex={-1}
            ref={containerRef}
          >
            <div className={styles.topBorder} />

            <button
              type='button'
              className={styles.closeButton}
              onClick={close}
              aria-label='Close feedback'
            >
              <Icon name='closeSm' size='m' />
            </button>

            <AnimatePresence mode='wait'>
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.18 }}
              >
                {step === 'thanks' ? (
                  <Typography variant='body-m' className={styles.primaryText}>
                    Thank you for your feedback!
                  </Typography>
                ) : (
                  <>
                    {questionMeta && (
                      <Typography
                        variant='label'
                        className={styles.secondaryText}
                      >
                        {`Question ${questionMeta.current} of ${questionMeta.total}`}
                      </Typography>
                    )}

                    {CurrentQuestion ? (
                      <CurrentQuestion
                        kitId={kitId}
                        feedbackType={feedbackType}
                        extraInfo={extraInfo}
                        onDone={onQuestionDone}
                      />
                    ) : null}
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
